import { observer } from 'mobx-react-lite'
import { type ReactElement, useMemo } from 'react'
import { TitledRow } from 'shared/ui/TitledRow/TitledRow'
import { Alert, Typography } from 'shared/ui'
import { pluralize } from 'shared/lib'
import { type CombinedPriceInfoStore } from 'entities/Billing'
import { getBillingLabel } from 'entities/Billing/lib/billingInterval'
import { PriceInfo, type IGracePeriodInfo } from 'entities/Billing/ui/PriceInfo/ui/PriceInfo'

interface ICombinedPriceInfoContentProps {
  priceInfoStore: CombinedPriceInfoStore
  topTitleContent?: ReactElement
  gracePeriodInfo?: IGracePeriodInfo
}

const CombinedPriceInfoContent = observer(
  ({ priceInfoStore, topTitleContent, gracePeriodInfo }: ICombinedPriceInfoContentProps) => {
    const { priceInfoUiData } = priceInfoStore

    const title = useMemo(() => {
      if (!priceInfoUiData?.tierDetails) return null

      return (
        <>
          {topTitleContent}
          {priceInfoUiData.tierDetails.map((details) => {
            const isSeats = details.type === 'seat'
            const label = isSeats ? 'Seats' : 'Number licenses'

            return (
              <TitledRow
                key={`${details.type}-${details.tier}`}
                title={`${label} × ${details.count}`}
                titleProps={{
                  color: 'var(--content-primary-primary)',
                  variant: 'body-md-regular',
                }}
              >
                <Typography ariaLabel={'TierPrice'} variant={'body-md-regular'}>
                  {priceInfoUiData.interval &&
                    `${details.perPrice} / ${getBillingLabel(priceInfoUiData.interval)}`}
                </Typography>
              </TitledRow>
            )
          })}
        </>
      )
    }, [priceInfoUiData, topTitleContent])

    if (!priceInfoUiData?.tierDetails) return null

    return (
      <PriceInfo priceInfoStore={priceInfoStore} gracePeriodInfo={gracePeriodInfo} title={title} />
    )
  }
)

interface ICombinedPriceInfoProps {
  priceInfoStore: CombinedPriceInfoStore
  gracePeriodInfo?: IGracePeriodInfo
}

export const CombinedPriceInfo = observer(
  ({ priceInfoStore, gracePeriodInfo }: ICombinedPriceInfoProps) => {
    const {
      seatsRequired,
      licensesRequired,
      seatsAvailable,
      licensesAvailable,
      seatsToCharge,
      licensesToCharge,
    } = priceInfoStore

    const needsSeats = seatsRequired > 0
    const needsLicenses = licensesRequired > 0

    const hasAvailableSeats = needsSeats && seatsAvailable > 0
    const hasAvailableLicenses = needsLicenses && licensesAvailable > 0
    const needsSeatsCharge = seatsToCharge > 0
    const needsLicensesCharge = licensesToCharge > 0

    const freeSeatsCount = Math.min(seatsAvailable, seatsRequired)
    const freeLicensesCount = Math.min(licensesAvailable, licensesRequired)

    const memberText = pluralize(seatsRequired, 'this member', 'these members')
    const numberText = pluralize(licensesRequired, 'this number', 'these numbers')

    const chargedMembersText = `${seatsToCharge} ${pluralize(seatsToCharge, 'member', 'members')}`
    const chargedNumbersText = `${licensesToCharge} ${pluralize(
      licensesToCharge,
      'number',
      'numbers'
    )}`

    const seatsAlert = hasAvailableSeats ? (
      <Alert
        item={{
          type: 'infoBlue',
          title: needsSeatsCharge
            ? `You'll be charged only for ${chargedMembersText}.`
            : `You'll not be charged for ${memberText}.`,
          desc: `Your workspace has ${seatsAvailable} ${pluralize(
            seatsAvailable,
            'seat',
            'seats'
          )} available.`,
        }}
        marginBottom={16}
      />
    ) : null

    const licensesAlert = hasAvailableLicenses ? (
      <Alert
        item={{
          type: 'infoBlue',
          title: needsLicensesCharge
            ? `You'll be charged only for ${chargedNumbersText}.`
            : `You'll not be charged for ${numberText}.`,
          desc: `Your workspace has ${licensesAvailable} number ${pluralize(
            licensesAvailable,
            'license',
            'licenses'
          )} available.`,
        }}
        marginBottom={needsSeatsCharge || needsLicensesCharge ? 16 : 0}
      />
    ) : null

    const freeSeatsRow =
      hasAvailableSeats && (needsSeatsCharge || needsLicensesCharge) ? (
        <TitledRow
          title={`Seats × ${freeSeatsCount}`}
          titleProps={{
            color: 'var(--content-primary-primary)',
            variant: 'body-md-regular',
          }}
        >
          <Typography ariaLabel={'FreeSeats'} variant={'body-md-regular'} color={'var(--green-80)'}>
            FREE
          </Typography>
        </TitledRow>
      ) : null

    const freeLicensesRow =
      hasAvailableLicenses && (needsSeatsCharge || needsLicensesCharge) ? (
        <TitledRow
          title={`Number licenses × ${freeLicensesCount}`}
          titleProps={{
            color: 'var(--content-primary-primary)',
            variant: 'body-md-regular',
          }}
        >
          <Typography
            ariaLabel={'FreeLicenses'}
            variant={'body-md-regular'}
            color={'var(--green-80)'}
          >
            FREE
          </Typography>
        </TitledRow>
      ) : null

    const topTitleContent =
      freeSeatsRow || freeLicensesRow ? (
        <>
          {freeSeatsRow}
          {freeLicensesRow}
        </>
      ) : undefined

    if (!needsSeatsCharge && !needsLicensesCharge) {
      return (
        <>
          {licensesAlert}
          {seatsAlert}
        </>
      )
    }

    return (
      <>
        {licensesAlert}
        {seatsAlert}
        <CombinedPriceInfoContent
          priceInfoStore={priceInfoStore}
          topTitleContent={topTitleContent}
          gracePeriodInfo={gracePeriodInfo}
        />
      </>
    )
  }
)
